import { ChangeEvent, FocusEvent, useState, useEffect } from "react";

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  step?: string;
}

export const NumberInput = ({ value, onChange, placeholder, disabled, className }: NumberInputProps) => {
  // Round the initial value to 2 decimal places
  const initialValue = value !== undefined && value !== null ? (Math.round(value * 100) / 100).toString().replace(".", ",") : "";

  const [inputValue, setInputValue] = useState(initialValue);

  // Update inputValue when value prop changes
  useEffect(() => {
    if (value !== undefined && value !== null) {
      // Round the value to 2 decimal places
      const roundedValue = Math.round(value * 100) / 100;
      setInputValue(roundedValue.toString().replace(".", ","));
    }
  }, [value]);

  const formatNumber = (x: string) => {
    // Handle European format: dots for thousands, commas for decimals
    const parts = x.split(",");
    const integerPart = parts[0];
    const decimalPart = parts.length > 1 ? parts[1] : "";

    // Format integer part with dots as thousand separators
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    // Return with comma for decimal if there is a decimal part
    return decimalPart ? `${formattedInteger},${decimalPart}` : formattedInteger;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Allow input to have numbers, dots, and one comma
    if (/^[0-9.,]*$/.test(input) && (input.match(/,/g) || []).length <= 1) {
      setInputValue(input);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Convert European format to standard format for parsing
    const standardFormat = input.replace(/\./g, "").replace(",", ".");
    const numericValue = parseFloat(standardFormat);

    if (!isNaN(numericValue)) {
      // Round to 2 decimal places
      const roundedValue = Math.round(numericValue * 100) / 100;
      onChange(roundedValue);

      // Format the rounded value with European format
      const roundedString = roundedValue.toString();
      const [integer, decimal] = roundedString.split(".");
      const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      const formattedValue = decimal ? `${formattedInteger},${decimal}` : formattedInteger;

      setInputValue(formattedValue);
    } else {
      setInputValue("");
    }
  };

  return (
    <input type="text" value={inputValue} onChange={handleChange} onBlur={handleBlur} placeholder={placeholder} disabled={disabled} className={className} inputMode="decimal" />
  );
};
